"use client";

import { ExternalLink, MessageSquare } from "lucide-react";
import { WaitlistModal } from "@/components/waitlist-modal";

const validationPainSignals = [
  {
    title: "How do you really validate your startup idea before touching code?",
    description:
      "I've seen so many founders (myself included once) spend months building an MVP, only to realize no one asked for it.",
    sourceLabel:
      "r/SaaS: How do you really validate your startup idea before touching code?",
    sourceUrl:
      "https://www.reddit.com/r/SaaS/comments/1nzgoa1/how_do_you_really_validate_your_startup_idea/",
  },
  {
    title: "How do you validate a SaaS idea before building anything?",
    description:
      "Working on a new project and trying to avoid the classic mistake of building something nobody wants.",
    sourceLabel:
      "r/SaaS: How do you validate a SaaS idea before building anything?",
    sourceUrl:
      "https://www.reddit.com/r/SaaS/comments/1rjo5vl/how_do_you_validate_a_saas_idea_before_building/",
  },
  {
    title:
      "How do you actually validate a product idea without wasting months and $$$?",
    description:
      "I've been thinking a lot about how painful early market validation can be... things like running surveys no one answers, hiring consultants that charge $10k+ for a slide deck etc. all to try to \u201cguess\u201d product-market fit like some kind of startup tarot reading.",
    sourceLabel:
      "r/SaaS: How do you actually validate a product idea without wasting months and $$$?",
    sourceUrl:
      "https://www.reddit.com/r/ProductManagement/comments/1mvc5v2/how_do_you_actually_validate_a_product_idea/",
  },
  {
    title:
      "Does building a quick landing page & running ads to validate an idea actually work",
    description:
      "someone replied : IME it's important to have a landing page; but don't drive traffic with ads. Make a lander/domain. Then find the early adopters and bring them in manually \u2014 do the actual customer discovery / customer-problem-fit and route them there.",
    sourceLabel:
      "r/indiehackers: Does building a quick landing page & running ads to validate an idea actually work",
    sourceUrl:
      "https://www.reddit.com/r/Entrepreneur/comments/1rxoany/does_building_a_quick_landing_page_running_ads_to/",
  },
];

export function SocialProofSection() {
  return (
    <section
      id="evidence"
      role="region"
      aria-label="Founder validation evidence"
      className="relative overflow-hidden py-16 lg:py-24"
      style={{ background: "#fbf9f5" }}
    >
      {/* Soft tonal atmosphere layer */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(196,149,106,0.06) 0%, transparent 70%)",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        {/* Editorial header — centered */}
        <div className="mb-14 text-center lg:mb-20">
          <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.18em] text-[#8a5e38]">
            Recurring patterns
          </p>
          <h2
            className="font-serif text-4xl font-bold tracking-tight text-[#2d2d2d] sm:text-5xl lg:text-[56px]"
            style={{ lineHeight: 1.1, letterSpacing: "-0.02em" }}
          >
            Founders keep hitting the
            <br className="hidden sm:block" />
            <span className="text-[#8a5e38]"> same validation wall</span>
          </h2>
          <p
            className="mx-auto mt-6 max-w-xl font-mono text-sm leading-[1.75] text-[#787878]"
            style={{ fontSize: "15px" }}
          >
            Recurring pain patterns from public startup threads. Not polished
            success stories &mdash; raw evidence of a real problem.
          </p>
        </div>

        {/* Evidence cards — staggered/floating composition */}
        <div className="relative mx-auto mb-16 max-w-5xl space-y-8 lg:mb-20">
          {validationPainSignals.map((signal, index) => {
            // Stagger: odd cards shift left, even cards shift right on desktop
            const isLeft = index % 2 === 0;
            return (
              <article
                key={signal.title}
                className={`
                  group relative rounded-lg border bg-white px-7 py-7
                  transition-all duration-200 hover:border-[#c4956a]/40
                  sm:px-8 sm:py-8
                  lg:relative lg:w-[90%]
                  ${isLeft ? "lg:ml-0" : "lg:ml-[10%]"}
                `}
                style={{
                  borderColor: "rgba(196,149,106,0.2)",
                  borderWidth: "1px",
                }}
              >
                {/* Top accent bar */}
                <div
                  aria-hidden="true"
                  className="absolute left-7 top-0 h-px w-12 bg-[#c4956a] sm:left-8 lg:left-7"
                  style={{ top: "-1px" }}
                />

                {/* Signal index + icon row */}
                <div className="mb-5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span
                      className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#c4956a]/60"
                      style={{ fontFamily: "'Space Grotesk', monospace" }}
                    >
                      Signal {String(index + 1).padStart(2, "0")}
                    </span>
                    <div
                      className="h-px flex-1 bg-[#e0dbd2]"
                      style={{ width: "40px" }}
                    />
                  </div>
                  <div
                    className="flex size-7 items-center justify-center rounded"
                    style={{ background: "rgba(196,149,106,0.1)" }}
                  >
                    <MessageSquare
                      className="size-3.5 text-[#c4956a]"
                      aria-hidden="true"
                    />
                  </div>
                </div>

                {/* Quote — evidence summary */}
                <blockquote>
                  <h3 className="mb-3 font-serif text-lg font-semibold leading-snug text-[#2d2d2d] sm:text-xl">
                    {signal.title}
                  </h3>
                  <p className="font-mono text-[13px] leading-[1.75] text-[#787878]">
                    &ldquo;{signal.description}&rdquo;
                  </p>
                </blockquote>

                {/* Source row */}
                <a
                  href={signal.sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-6 inline-flex items-center gap-2.5 font-mono text-[11px] text-[#8a5e38] transition-colors hover:text-[#c4956a] sm:text-[12px]"
                  style={{ fontFamily: "'Space Grotesk', monospace" }}
                >
                  <span className="inline-flex size-5 shrink-0 items-center justify-center rounded-sm bg-[#ff4500]/15 text-[10px] font-bold text-[#ff4500]">
                    r/
                  </span>
                  <span className="uppercase tracking-[0.1em] text-[#a06f43]">
                    Source
                  </span>
                  <span className="text-[#c4956a]/35">&mdash;</span>
                  <span className="max-w-[180px] truncate text-[#9b7651] sm:max-w-[280px]">
                    {signal.sourceLabel}
                  </span>
                  <ExternalLink
                    className="size-3 shrink-0 text-[#c4956a]/45"
                    aria-hidden="true"
                  />
                </a>
              </article>
            );
          })}
        </div>

        {/* CTA — visually consistent */}
        <div className="flex flex-col items-center gap-4">
          <div className="flex items-center gap-4">
            <div className="h-px w-10 bg-[#e0dbd2]" />
            <span
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#c4956a]/50"
              style={{ fontFamily: "'Space Grotesk', monospace" }}
            >
              Continue evidence collection
            </span>
            <div className="h-px w-10 bg-[#e0dbd2]" />
          </div>
          <WaitlistModal
            triggerText="Apply as an evidence-first founder"
            analyticsSource="social_proof_section"
          />
        </div>
      </div>
    </section>
  );
}
